import {
  parsePasPracticeLibrary,
  type PasPracticeLibraryItem,
} from './pasPracticeLibrary';

/** PAS (实战答题纸) helpers — aligned with Mobile2 pasQrAnchor. */

export type PasQrFormat = 'pas_json_v1' | 'detail_url' | 'particle_id';

export type PasQrAnchor = {
  kpId: string;
  planId?: string;
  sessionId?: string;
  batch?: string;
  format: PasQrFormat;
  raw: string;
};

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function isPasAssetType(type: string | null | undefined): boolean {
  return String(type || '').trim().toLowerCase() === 'pas';
}

export function readPasAnchorFromAssetMetadata(
  metadata: Record<string, unknown> | null | undefined,
): PasQrAnchor | null {
  const raw = metadata?.pasAnchor;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const kpId = asNonEmptyString(record.kpId);
  if (!kpId) {
    return null;
  }

  const formatRaw = asNonEmptyString(record.format);
  const format: PasQrFormat =
    formatRaw === 'pas_json_v1' || formatRaw === 'detail_url' || formatRaw === 'particle_id'
      ? formatRaw
      : 'particle_id';

  return {
    kpId,
    planId: asNonEmptyString(record.planId) || undefined,
    sessionId: asNonEmptyString(record.sessionId) || undefined,
    batch: asNonEmptyString(record.batch) || undefined,
    format,
    raw: asNonEmptyString(record.raw) || kpId,
  };
}

/** Group 0 in math ladder library — DRS from five anchor entrances (no canvas crop). */
export const PAS_ANCHOR_PRACTICE_SEQUENCE_ID = '__pas_anchor_drs__';

export function isPasAnchorPracticeSequenceId(id: string | null | undefined): boolean {
  return String(id || '').trim() === PAS_ANCHOR_PRACTICE_SEQUENCE_ID;
}

export type PasWorkbenchGateStatus = 'idle' | 'judging' | 'passed' | 'failed';

export type PasWorkbenchRegion = {
  id: string;
  rectNormalized: { x: number; y: number; width: number; height: number };
  gateStatus: PasWorkbenchGateStatus;
  gateTheme?: string;
  /** Ordinary-asset deliberate practice: related KP from this asset's extraction. */
  themeKpId?: string;
  /** Cached crop → qwen vision text from admit. */
  regionContent?: string;
  regionFeedback?: string;
  practiceDraftId?: string;
  /** Append-only practice library for this QG node (expire in place, never delete). */
  practiceItems?: PasPracticeLibraryItem[];
};

function asFiniteNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Read persisted PAS canvas regions from asset.metadata.pasWorkbench */
export function readPasWorkbenchRegionsFromAssetMetadata(
  metadata: Record<string, unknown> | null | undefined,
): PasWorkbenchRegion[] {
  const raw = metadata?.pasWorkbench;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return [];
  }

  const regionsRaw = (raw as Record<string, unknown>).regions;
  if (!Array.isArray(regionsRaw)) {
    return [];
  }

  const out: PasWorkbenchRegion[] = [];
  for (const item of regionsRaw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const record = item as Record<string, unknown>;
    const id = asNonEmptyString(record.id);
    const rect = record.rectNormalized;
    if (!id || !rect || typeof rect !== 'object' || Array.isArray(rect)) continue;
    const rectRecord = rect as Record<string, unknown>;
    const x = asFiniteNumber(rectRecord.x);
    const y = asFiniteNumber(rectRecord.y);
    const width = asFiniteNumber(rectRecord.width);
    const height = asFiniteNumber(rectRecord.height);
    if (x == null || y == null || width == null || height == null) continue;
    const statusRaw = asNonEmptyString(record.gateStatus) || 'idle';
    const gateStatus: PasWorkbenchGateStatus =
      statusRaw === 'judging' || statusRaw === 'passed' || statusRaw === 'failed' || statusRaw === 'idle'
        ? statusRaw
        : 'idle';
    const practiceItems = parsePasPracticeLibrary(record.practiceItems);
    out.push({
      id,
      rectNormalized: { x, y, width, height },
      gateStatus,
      gateTheme: asNonEmptyString(record.gateTheme) || undefined,
      themeKpId: asNonEmptyString(record.themeKpId)
        || asNonEmptyString(record.sourceKpId)
        || undefined,
      regionContent: asNonEmptyString(record.regionContent) || undefined,
      regionFeedback: asNonEmptyString(record.regionFeedback) || undefined,
      practiceDraftId: asNonEmptyString(record.practiceDraftId) || undefined,
      practiceItems: practiceItems.length > 0 ? practiceItems : undefined,
    });
  }
  return out;
}

/** Group 0 practice library — five entrance DRS paths on the whole asset. */
export function readPasAnchorPracticeItems(
  metadata: Record<string, unknown> | null | undefined,
): PasPracticeLibraryItem[] {
  const raw = metadata?.pasWorkbench;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return [];
  }
  return parsePasPracticeLibrary((raw as Record<string, unknown>).anchorPracticeItems);
}

function truncateLabel(text: string, maxChars: number) {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= maxChars) return trimmed;
  return `${trimmed.slice(0, Math.max(1, maxChars - 1))}…`;
}

export type PasPracticeThemeLink = {
  themeKpId: string;
  regionIds: string[];
  practiceItemCount: number;
  practiceNodeCount: number;
};

/** Map ordinary KPs → deliberate-practice regions on the same asset (for list deep-links). */
export function summarizePasPracticeLinksByThemeKp(
  metadata: Record<string, unknown> | null | undefined,
): Map<string, PasPracticeThemeLink> {
  const regions = readPasWorkbenchRegionsFromAssetMetadata(metadata);
  const map = new Map<string, PasPracticeThemeLink>();
  for (const region of regions) {
    const themeKpId = String(region.themeKpId || '').trim();
    if (!themeKpId) continue;
    const prev = map.get(themeKpId) || {
      themeKpId,
      regionIds: [] as string[],
      practiceItemCount: 0,
      practiceNodeCount: 0,
    };
    prev.regionIds.push(region.id);
    if (region.gateStatus === 'passed') prev.practiceNodeCount += 1;
    if (Array.isArray(region.practiceItems)) {
      prev.practiceItemCount += region.practiceItems.length;
    }
    map.set(themeKpId, prev);
  }
  return map;
}

/** Feed card summary: hatch themes + practice variant count from pasWorkbench. */
export function summarizePasWorkbenchForCard(
  metadata: Record<string, unknown> | null | undefined,
): {
  hatchTitle: string | null;
  practiceItemCount: number;
  practiceNodeCount: number;
} {
  const regions = readPasWorkbenchRegionsFromAssetMetadata(metadata);
  const themes: string[] = [];
  let practiceItemCount = readPasAnchorPracticeItems(metadata).length;
  let practiceNodeCount = 0;
  for (const region of regions) {
    if (region.gateStatus === 'passed') practiceNodeCount += 1;
    const theme = asNonEmptyString(region.gateTheme);
    if (theme) themes.push(theme);
    practiceItemCount += Array.isArray(region.practiceItems) ? region.practiceItems.length : 0;
  }
  const uniqueThemes = [...new Set(themes)];
  let hatchTitle: string | null = null;
  if (uniqueThemes.length === 1) {
    hatchTitle = truncateLabel(uniqueThemes[0]!, 48);
  } else if (uniqueThemes.length > 1) {
    hatchTitle = uniqueThemes.slice(0, 2).map((theme) => truncateLabel(theme, 22)).join(' · ');
  }
  return { hatchTitle, practiceItemCount, practiceNodeCount };
}

export function findPasSearchRegionId(
  asset: {
    metadata?: unknown;
    description?: string | null;
    ai_title?: string | null;
    ai_brief?: string | null;
  },
  query: string,
): string | null {
  const needle = query.trim().toLowerCase();
  if (!needle) return null;
  const metadata = asset.metadata && typeof asset.metadata === 'object' && !Array.isArray(asset.metadata)
    ? asset.metadata as Record<string, unknown>
    : null;
  const regions = readPasWorkbenchRegionsFromAssetMetadata(metadata);
  for (const region of regions) {
    const haystack = [
      region.gateTheme,
      region.regionContent,
      region.regionFeedback,
      ...(region.practiceItems || []).flatMap((item) => [
        item.question,
        item.answer,
        item.notes,
        item.entranceKind,
      ]),
    ].map((value) => String(value || '').toLowerCase());
    if (haystack.some((text) => text.includes(needle))) {
      return region.id;
    }
  }
  return regions[0]?.id || null;
}

export function buildPasStairsHref(assetId: string, regionId?: string | null): string {
  const params = new URLSearchParams();
  if (regionId) params.set('regionId', regionId);
  const qs = params.toString();
  const base = `/assets/${encodeURIComponent(assetId)}/practice`;
  return qs ? `${base}?${qs}` : base;
}
